// Product image URLs must use a small fixed set of dimensions so the CDN can
// cache them: raw container-size × devicePixelRatio values are near-unique per
// device/viewport and defeat the cache (VIP-12050). The image API resizes with
// bestfit (aspect-preserving, fits within the requested box), so snapping up
// to the next ladder step is visually lossless.
const IMAGE_SIZE_LADDER = [110, 165, 220, 330, 440, 660, 880, 1320]

/**
 * @param {Number} size
 * @returns {Number} the smallest ladder step >= size (largest step if size exceeds the ladder)
 */
const snapImageSize = (size) =>
  IMAGE_SIZE_LADDER.find((step) => step >= size) ?? IMAGE_SIZE_LADDER.at(-1)

const isRenderableSize = (size) => Number.isFinite(size) && size > 0

/**
 * Builds the product image URL query string, snapping dimensions to the size
 * ladder. All product image URLs must go through this so no call site can
 * reintroduce per-device dimensions that fragment the CDN cache.
 *
 * @param {String} type image type, e.g. 'image' or 'small_image'
 * @param {Number} height desired height in physical pixels (omitted from the URL if not a positive finite number)
 * @param {Number} width desired width in physical pixels (omitted from the URL if not a positive finite number)
 * @returns {String} appendix like '?type=small_image&height=330&width=330&filetype=webp'
 */
const buildImageUrlAppendix = (type, height, width) => {
  let appendix = `?type=${type}`

  if (isRenderableSize(height)) {
    appendix += `&height=${snapImageSize(height)}`
  }

  if (isRenderableSize(width)) {
    appendix += `&width=${snapImageSize(width)}`
  }

  return `${appendix}&filetype=webp`
}

export { IMAGE_SIZE_LADDER, buildImageUrlAppendix, snapImageSize }
